import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import StatusBadge from "@/components/common/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const SYSTEMS = [
  { value: "access_control", label: "Access Control" },
  { value: "vms",            label: "VMS" },
  { value: "radar",          label: "Radar" },
];
const STATUSES = [
  { value: "planning",    label: "Planning" },
  { value: "in_progress", label: "In progress" },
  { value: "completed",   label: "Completed" },
];

export default function ProjectsPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", client: "", system: "access_control", site: "", status: "planning", progress: 0, engineer: "", region: "", budget: 0 });

  const load = useCallback(() => {
    return api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const canCreate = ["admin", "staff"].includes(user?.role);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/projects", { ...form, progress: Number(form.progress), budget: Number(form.budget) });
      toast.success("Project created.");
      setOpen(false);
      setForm({ name: "", client: "", system: "access_control", site: "", status: "planning", progress: 0, engineer: "", region: "", budget: 0 });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-6 lg:p-10 space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-overline">02 · {t("projects.title")}</div>
          <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight text-white mt-2" data-testid="projects-title">{t("projects.title")}</h1>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="inline-flex items-center gap-2 px-5 h-10 rounded-sm bg-primary text-primary-foreground font-medium hover:bg-[#33B9FF] transition-colors" data-testid="projects-new-button">
                <Plus className="w-4 h-4" /> {t("projects.new")}
              </button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader><DialogTitle className="text-white font-mono tracking-widest uppercase text-sm">{t("projects.new")}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="project-form">
                <F label="Project name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} data-testid="project-name-input" /></F>
                <F label="Client"><input value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} className={inp} data-testid="project-client-input" /></F>
                <div className="grid grid-cols-2 gap-3">
                  <F label="System"><select value={form.system} onChange={(e) => setForm({ ...form, system: e.target.value })} className={inp} data-testid="project-system-select">{SYSTEMS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></F>
                  <F label="Status"><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inp} data-testid="project-status-select">{STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></F>
                </div>
                <F label="Site"><input required value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} className={inp} data-testid="project-site-input" /></F>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Engineer"><input value={form.engineer} onChange={(e) => setForm({ ...form, engineer: e.target.value })} className={inp} data-testid="project-engineer-input" /></F>
                  <F label="Progress %"><input type="number" min={0} max={100} value={form.progress} onChange={(e) => setForm({ ...form, progress: e.target.value })} className={inp} data-testid="project-progress-input" /></F>
                </div>
                <DialogFooter>
                  <button type="submit" className="h-10 px-5 rounded-sm bg-primary text-primary-foreground font-medium" data-testid="project-form-submit">{t("common.save")}</button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </header>

      <div className="border border-border rounded-sm bg-card overflow-hidden">
        <table className="w-full text-left" data-testid="projects-table">
          <thead>
            <tr className="border-b border-border text-[10px] font-mono tracking-[0.22em] text-muted-foreground uppercase">
              <th className="px-6 py-3">{t("projects.columns.name")}</th>
              <th className="px-4 py-3">{t("projects.columns.system")}</th>
              <th className="px-4 py-3">{t("projects.columns.site")}</th>
              <th className="px-4 py-3">{t("projects.columns.engineer")}</th>
              <th className="px-4 py-3 w-48">{t("projects.columns.progress")}</th>
              <th className="px-4 py-3 text-right pr-6">{t("projects.columns.status")}</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.project_id} className="border-b border-border/60 last:border-b-0 hover:bg-secondary/40 transition-colors" data-testid={`project-row-${p.project_id}`}>
                <td className="px-6 py-4">
                  <div className="text-sm text-white">{p.name}</div>
                  <div className="text-[11px] font-mono tracking-widest text-muted-foreground uppercase mt-0.5">{p.client || "—"}</div>
                </td>
                <td className="px-4 py-4 text-sm text-secondary-foreground font-mono uppercase tracking-widest text-[11px]">{p.system.replace("_", " ")}</td>
                <td className="px-4 py-4 text-sm text-secondary-foreground">{p.site}</td>
                <td className="px-4 py-4 text-sm text-secondary-foreground font-mono">{p.engineer || "—"}</td>
                <td className="px-4 py-4">
                  <div className="h-1 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-primary" style={{ width: `${p.progress}%` }} /></div>
                  <div className="mt-1 font-mono text-[10px] tracking-widest text-muted-foreground">{p.progress}%</div>
                </td>
                <td className="px-4 py-4 text-right pr-6"><StatusBadge status={p.status} /></td>
              </tr>
            ))}
            {projects.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-sm text-muted-foreground">No projects.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const inp = "w-full h-10 px-3 rounded-sm bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none font-mono text-sm text-white";
function F({ label, children }) {
  return <label className="block"><div className="mb-1.5 text-[10px] font-mono tracking-[0.22em] text-muted-foreground uppercase">{label}</div>{children}</label>;
}
